import { SettingsVerticalIcon } from 'components/icons/SettingsVertical'
import { ChevronLeft } from 'components/icons/ChevronLeft'
import { ChevronRight } from 'components/icons/ChevronRight'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { activeTools } from './hooks'
import type { QATool } from './hooks'
import classNames from 'classnames'

type ToolEntry = [string, QATool]

const groupSlug = (group: string) =>
  group.toLowerCase().replace(/[^a-z0-9]+/g, '-')

function ToolRow({ label, callback }: QATool) {
  if (typeof label === 'string') {
    return (
      <a
        className="cursor-pointer p-2 hover:bg-gray-300"
        onClick={async () => {
          const keepOpen = await callback()
          if (keepOpen) return
          activeTools.hide()
        }}
      >
        {label}
      </a>
    )
  }

  return <div>{label}</div>
}

export const QATools = observer(function QATools() {
  const open = activeTools.enabled.get()
  const tools = activeTools.tools
  const [location, setLocation] = useState<'top-left' | 'top-right'>('top-left')

  useEffect(() => {
    // listen to shift-Q to toggle the QA tools
    const listener = (e: KeyboardEvent) => {
      // detect that we're not in an input field
      if (e.target instanceof HTMLInputElement) return
      if (e.target instanceof HTMLTextAreaElement) return

      if (e.key === 'Q' && e.shiftKey) {
        activeTools.toggle()
      }
    }

    document.addEventListener('keydown', listener)

    return () => {
      document.removeEventListener('keydown', listener)
    }
  }, [tools])

  if (tools.size === 0) return null

  const toolsArray = Array.from(tools).sort((a, b) => {
    return a[0].localeCompare(b[0])
  })

  // Ungrouped tools first, in key order; then one row per group, in name
  // order. The group row toggles a sub-panel beside the main list.
  const ungrouped: ToolEntry[] = []
  const grouped = new Map<string, ToolEntry[]>()
  for (const entry of toolsArray) {
    const group = entry[1].group
    if (!group) {
      ungrouped.push(entry)
      continue
    }
    const members = grouped.get(group) ?? []
    members.push(entry)
    grouped.set(group, members)
  }
  const groupNames = Array.from(grouped.keys()).sort((a, b) =>
    a.localeCompare(b)
  )
  const expandedGroup = activeTools.expandedGroup.get()
  const expandedTools = expandedGroup ? grouped.get(expandedGroup) : undefined
  // The sub-panel opens toward the free side of the screen: right of the
  // list when docked top-left, left of it when docked top-right.
  const ExpandIcon = location === 'top-left' ? ChevronRight : ChevronLeft

  return (
    <div
      className={classNames('fixed z-[9999] min-w-[300px]', {
        'left-0 top-0': location === 'top-left',
        'right-0 top-0': location === 'top-right',
      })}
    >
      <div
        className={classNames('flex max-h-full bg-white shadow-lg', {
          'flex-row rounded-br-xl': location === 'top-left',
          'flex-row-reverse rounded-bl-xl': location === 'top-right',
        })}
      >
        {!open && (
          <div
            className="flex cursor-pointer flex-row items-center gap-1 p-2 text-label-medium text-on-surface-var"
            onClick={() => activeTools.toggle()}
          >
            <SettingsVerticalIcon size={14} />
            Settings
          </div>
        )}
        {open && (
          <div className="flex max-h-screen min-w-[300px] flex-col flex-wrap text-body-medium">
            <div
              className="flex cursor-pointer flex-row items-center gap-1 p-2 text-label-medium text-on-surface-var"
              onClick={() => activeTools.toggle()}
            >
              <SettingsVerticalIcon size={14} />
              Settings
            </div>
            <a
              className="cursor-pointer p-2 hover:bg-gray-300"
              onClick={async () => {
                setLocation(location === 'top-left' ? 'top-right' : 'top-left')
              }}
            >
              Change Tools Corner
            </a>
            {ungrouped.map(([name, tool]) => (
              <ToolRow key={name} {...tool} />
            ))}
            {groupNames.map((group) => {
              const expanded = group === expandedGroup
              return (
                <a
                  className={classNames(
                    'flex cursor-pointer flex-row items-center justify-between gap-1 p-2 hover:bg-gray-300',
                    { 'bg-gray-200': expanded }
                  )}
                  key={group}
                  data-testid={`qa-tools-group-${groupSlug(group)}`}
                  role="button"
                  tabIndex={0}
                  aria-expanded={expanded}
                  onClick={() => activeTools.toggleGroup(group)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return
                    e.preventDefault()
                    activeTools.toggleGroup(group)
                  }}
                >
                  {group}
                  <ExpandIcon size={14} />
                </a>
              )
            })}
          </div>
        )}
        {open && expandedGroup && expandedTools && (
          <div
            className={classNames(
              'border-outline-variant flex max-h-screen min-w-[300px] flex-col flex-wrap text-body-medium',
              {
                'border-l': location === 'top-left',
                'border-r': location === 'top-right',
              }
            )}
            data-testid={`qa-tools-group-panel-${groupSlug(expandedGroup)}`}
          >
            <div className="p-2 text-label-medium text-on-surface-var">
              {expandedGroup}
            </div>
            {expandedTools.map(([name, tool]) => (
              <ToolRow key={name} {...tool} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
})
